import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, viewChild } from '@angular/core';
import { DialogModule } from 'primeng/dialog';
import { NgOptimizedImage } from '@angular/common';
import { PopoverModule, Popover } from 'primeng/popover';
import { TableModule, TablePageEvent } from 'primeng/table';
import {
  SystemNotification,
  SystemNotificationGroup,
  SystemNotificationType,
  SystemNotificationsService,
} from '@/layouts/system-notifications/system-notifications.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs/operators';
import { BadgeModule } from 'primeng/badge';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime, merge, of } from 'rxjs';
import { NotificationQuery } from '@shared/models/notification-query';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { PersonalizationService } from '@/shared/services/personalization.service';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { TimeAgoPipe } from "../../shared/pipes/time-ago.pipe";

@Component({
  selector: 'cis-system-notifications',
  imports: [
    DialogModule,
    PopoverModule,
    TableModule,
    NgOptimizedImage,
    BadgeModule,
    TranslatePipe,
    ButtonModule,
    TooltipModule,
    TimeAgoPipe
  ],
  template: `
    @if (profile()) {
      @let count = notificationsCount();
      <div class="flex justify-content-center align-items-center">
        <span class="p-overlay-badge" (click)="op.toggle($event)">
          <i class="pi pi-bell text-3xl cursor-pointer"></i>
          @if (count > 0) {
            <p-badge severity="info" [value]="count" />
          }
        </span>
      </div>
      <p-popover
        #op
        [style]="{ width: '620px', maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }"
        (onShow)="onShow()">
        <div class="notification-header">
          <h5 class="m-0">{{ 'common.notifications' | translate }}</h5>
          <a (click)="$event.stopPropagation(); markAllAsRead()">{{ 'common.mark_all_as_read' | translate }}</a>
        </div>

        <p-table
          #dt
          lazy
          class="p-datatable"
          [value]="notifications()"
          [rowHover]="true"
          [paginator]="true"
          [rows]="formGroup.controls.pageSize.getRawValue()"
          [rowsPerPageOptions]="[5, 10, 25]"
          [totalRecords]="totalRecords()"
          paginatorStyleClass="justify-content-end"
          (onPage)="onPage($event)"
          [first]="(formGroup.controls.pageNumber.getRawValue() - 1) * formGroup.controls.pageSize.getRawValue()">
          <ng-template #body let-notification>
            <tr class="cursor-pointer" (click)="$event.stopPropagation(); navigateToEntity(notification)">
              <td class="p-0">
                <img ngSrc="/branding/logos/regular/SVG/Mark.svg" height="70" width="70" alt="fim" />
              </td>
              <td>
                <div class="notification-content">
                  <h5>
                    {{ notification.subject }}
                    @if (!notification.isRead) {
                      <p-button
                        size="small"
                        severity="success"
                        text
                        icon="pi pi-check"
                        [pTooltip]="'common.mark_as_read' | translate"
                        tooltipPosition="top"
                        (onClick)="$event.stopPropagation(); markAsRead(notification.id)" />
                    }
                  </h5>
                  <p [innerHTML]="notification.description"></p>
                  <span class="notification-time">{{ notification.createdDate | timeAgo }}</span>
                </div>
              </td>
            </tr>
          </ng-template>
          <ng-template #emptymessage>
            <tr>
              <td colspan="2">{{ 'notifications.no_notifications' | translate }}</td>
            </tr>
          </ng-template>
        </p-table>
      </p-popover>
    }
  `,
  styleUrls: ['system-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemNotificationsComponent {
  private readonly systemNotificationsService = inject(SystemNotificationsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly personalizationService = inject(PersonalizationService);

  profile = toSignal(this.personalizationService.profiles$.pipe(map((t) => t?.id ?? null)), {
    initialValue: null,
  });

  overlayPanel = viewChild<Popover>('op');

  formGroup = new FormGroup({
    pageNumber: new FormControl(1, { nonNullable: true }),
    pageSize: new FormControl(5, { nonNullable: true }),
    searchTerm: new FormControl<string | null>(null),
    orderByField: new FormControl<string | undefined>('createdDate', {
      nonNullable: true,
    }),
    isAsc: new FormControl<boolean>(false),
    showRead: new FormControl<boolean>(false, { nonNullable: true }),
  });

  notificationsResponse = toSignal(
    merge(
      this.formGroup.valueChanges.pipe(debounceTime(300)),
      this.systemNotificationsService.refresher$.pipe(map(() => this.formGroup.getRawValue())),
      // The active profile was read inside the switchMap below but was not part of this
      // merge, so the notification list and its badge never re-fetched on a role change.
      this.personalizationService.profiles$.pipe(map(() => this.formGroup.getRawValue())),
    ).pipe(
      map((value) => {
        const copyValue = { ...value };
        for (const k in copyValue) {
          const key = k as keyof typeof copyValue;
          if (copyValue[key] === undefined || copyValue[key] === null) {
            delete copyValue[key];
          }
        }
        return copyValue as NotificationQuery;
      }),
      switchMap((value) => {
        const profiile = this.profile();
        if (!profiile) {
          return of({
            data: {
              items: [],
              totalRecords: 0,
            },
          });
        }
        return this.systemNotificationsService.getByQuery(value);
      }),
    ),
  );

  totalRecords = computed(() => {
    const response = this.notificationsResponse();
    if (!response) {
      return 0;
    }
    return response.data.totalRecords;
  });

  notifications = computed(() => {
    const response = this.notificationsResponse();
    if (!response) {
      return [];
    }
    return response.data.items;
  });

  onPage(event: TablePageEvent) {
    const oldPageSize = this.formGroup.controls.pageSize.getRawValue();
    const newPageNumber = event.first / event.rows + 1;

    if (oldPageSize !== event.rows) {
      this.formGroup.controls.pageSize.patchValue(event.rows, { emitEvent: false });
      this.formGroup.controls.pageNumber.patchValue(1);
    } else if (this.formGroup.controls.pageNumber.getRawValue() !== newPageNumber) {
      this.formGroup.controls.pageNumber.patchValue(newPageNumber);
    }
  }

  markAllAsRead() {
    this.systemNotificationsService.markAllAsRead().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  markAsRead(id: number) {
    this.systemNotificationsService.markAsRead(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  notificationsCount = computed(() => this.notificationsResponse()?.data?.totalRecords ?? 0);

  navigateToEntity({ id, groupId, entityId, typeId }: SystemNotification) {
    this.markAsRead(id);
    let entityType: string;

    switch (groupId) {
      case SystemNotificationGroup.DataRequest:
        entityType = 'datarequests';
        break;
      case SystemNotificationGroup.QuestionnaireRequest:
        entityType = 'questionnaireRequests';
        break;
      default:
        entityType = 'datatemplates';
        break;
    }
    // NPS survey invitation (CR 47670) opens the questionnaire with the survey trigger.
    const extras =
      typeId === SystemNotificationType.QuestionnaireNpsSurvey ? { queryParams: { feedback: 1 } } : undefined;
    this.router.navigate([`/${entityType}/${entityId}`], extras).then(() => {
      this.overlayPanel()?.hide();
    });
  }

  onShow() {
    const content = document.getElementsByClassName('p-popover');
    setTimeout(() => {
      if (content) {
        content[0].scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }, 20);
  }
}
